// A richer sibling of HoverTooltip for "this is locked behind a plan"
// moments — an icon, a headline and a short reason, instead of one plain
// line of text. Renders into a portal so it's never clipped by an
// ancestor's overflow, same reasoning as HoverTooltip.
import React, { useCallback, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";

export default function UpsellTooltip({ title, subtitle, children, offset = 10 }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const navigate = useNavigate();
  const hideTimerRef = useRef(null);

  const show = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + offset, left: r.left + r.width / 2 });
  }, [offset]);

  // A short grace period — without it, moving the mouse straight down
  // through the gap between the trigger and the tooltip fires this before
  // the cursor ever reaches the tooltip's own onMouseEnter, closing it
  // mid-transit so it's never actually clickable.
  const hide = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setPos(null), 150);
  }, []);

  return (
    <span ref={ref} onMouseEnter={show} onMouseLeave={hide} className="inline-flex">
      {children}
      {pos &&
        ReactDOM.createPortal(
          <div
            role="tooltip"
            onMouseEnter={show}
            onMouseLeave={hide}
            style={{ position: "fixed", top: pos.top, left: pos.left, transform: "translateX(-50%)", zIndex: 9999 }}
          >
            <button
              type="button"
              onClick={() => navigate("/account/billing")}
              className="relative block w-[232px] rounded-[12px] border border-[#ECE9EE] bg-white px-3.5 py-3 text-left shadow-[0_12px_32px_rgba(26,13,27,0.16)] transition-colors hover:border-[#E3B8DE] hover:bg-[#FBF5FA]"
            >
              <span
                className="absolute left-1/2 -translate-x-1/2 bottom-full h-0 w-0"
                style={{ borderLeft: "7px solid transparent", borderRight: "7px solid transparent", borderBottom: "7px solid #ECE9EE" }}
              />
              <span
                className="absolute left-1/2 -translate-x-1/2 bottom-full mb-[-1px] h-0 w-0"
                style={{ borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderBottom: "6px solid #FFFFFF" }}
              />
              <div className="flex items-start gap-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F6E9F4] text-[#A04A97]">
                  <Lock size={13} />
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold leading-tight text-[#1A1A1A]">{title}</p>
                  {subtitle && <p className="mt-1 text-[11.5px] leading-snug text-[#8A8A90]">{subtitle}</p>}
                  <p className="mt-1.5 text-[11.5px] font-semibold text-[#A04A97]">View plans →</p>
                </div>
              </div>
            </button>
          </div>,
          document.body
        )}
    </span>
  );
}
